/**
 * @licence
 * @licstart
 * Copyright (C) 2020-2021 McAfee, LLC. All Rights Reserved.
 * @licend
 *
 * IAM-CACHE-CONTROL: max-age=60
 */

import { EmailPage } from './pages/email';
import { LoginPage } from './pages/login';
import { DashboardPage } from './pages/dashboard';
import { SignInHelpPage } from './pages/signinhelp';
import { UpdatePasswordPage } from './pages/updatepassword';
import { IsAlreadySignedInPage } from './pages/isalreadysignedin';
import { OktaSignIn } from '@okta/okta-signin-widget';
import { iamLocalization } from './localization';
import { Page } from './pages/page-base';
import { iam } from './iam';

export type LocalizationParameters = { [key: string]: string | number | object };

export interface LocalizeElementOptions {
  stringKey?: string,
  string?: string,
  stringParameters?: LocalizationParameters,
  localeSubstitution?: boolean,
  localeSeparator?: string,
  attribute?: string,
  rawHTML?: boolean,
  isWidgetElement?: boolean
}

export interface IamNotification {
  title?: string,
  subtitle?: string,
  text: string,
  priority: number,
  start?: string | Date,
  end?: string | Date,
  consoles?: string[]
}

export interface ConsoleApp {
  name: string,
  url: string
}

export const iamUtils = {
  escapeHtml: function (html: string) {
    return html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  },

  /**
   * Gets the position of one element relative to another element that's a container for a position: absolute element to
   * be placed around the element.
   *
   * @param element
   * @param container
   * @returns {{top, left, width, height}}
   */
  getRelativePosition: function (element: Element | string, container: Element | string) {
    if (typeof element === 'string') {
      element = document.getElementById(element);
    }
    if (typeof container === 'string') {
      container = document.getElementById(container);
    }
    const rectElement = element.getBoundingClientRect();
    const rectContainer = container.getBoundingClientRect();
    const pos = {
      top: rectElement.top - rectContainer.top,
      left: rectElement.left - rectContainer.left,
      height: rectElement.height,
      width: rectElement.width
    };
    return pos;
  },

  /**
   * Returns the current date/time (overridable for testing)
   * @returns {Date}
   */
  now: function () {
    return new Date();
  },

  addEventListeners: function <EventName extends keyof HTMLElementEventMap>(element: HTMLElement, events: EventName[], func: (event: HTMLElementEventMap[EventName]) => void) {
    events.forEach(function (event) {
      element.addEventListener(event, func);
    });
  },

  isValidUrl: function (url: string) {
    try {
      return Boolean(new URL(url));
    } catch (e) {
      return false;
    }
  }
};

  /**
   * Error codes for when there's no useful explanation for the user, but can be helpful in debugging the cause
   */
export const iamErrorCodes = {
  loadingScopes: 'oak',
  possibleScopes: 'maple',
  invalidRequest: 'pine',
  unexpectedError: 'fir'
};

class IamLogin {
  public storageEnabled = !this.isEmailPage(); // only really need to test this on the email page

  public pageInitialized = false;
  public page: Page = null;

  public isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  public isChrome() {
    return /chrome/i.test(navigator.userAgent);
  }

  public isEmailPage() {
    return iam.Token?.getLocation().pathname === '/' || iam.Token?.getLocation().pathname === '/email.html';
  }

  public isLoginPage() {
    return iam.Token.getLocation().pathname === '/login.html';
  }

  public isSignInHelpPage() {
    return iam.Token.getLocation().pathname === '/signInHelp.html';
  }

  public isDashboardPage() {
    return iam.Token.getLocation().pathname === '/dashboard.html';
  }

  public isUpdatePasswordPage() {
    return iam.Token.getLocation().pathname === '/updatepassword.html';
  }

  public isAlreadySignedInPage() {
    return iam.Token.getLocation().origin === iam.settings.signInPage && iam.Token.getLocation().pathname === '/alreadySignedIn.html';
  }  

  public async redirectBackToEmail(authCode?: { [key: string]: string }) {
    if (authCode) {
      console.log('[Auth Code] redirecting back to email with: ', authCode);
      iam.Token.removeItem('accessToken');
      iam.Token.removeItem('accessTokenExpires');
      await iam.Token.removeSessionCookie(); // IAM-7040 - kill session cookie on Dashboard page from Skyhigh product logout, triggering logout on other apps
      const url = (iam.settings.signInPage) + '?SAMLRequest=' + encodeURIComponent(authCode.SAMLRequest) + '&RelayState=' + encodeURIComponent(authCode.RelayState) + '&SigAlg=' + encodeURIComponent(authCode.SigAlg) + '&Signature=' + encodeURIComponent(authCode.Signature);
      // if (iam.settings.debug)
      console.log('[Auth Code] Redirecting back to email page: ' + url);
      iam.Token.setLocation(url);
    } else {
      const oktaSignIn = new OktaSignIn({ baseUrl: iam.settings.domain });
      let cb;
      oktaSignIn.authClient.closeSession().then(cb = function (err?: unknown) {
        if (err) {
          if (iam.settings.debug) console.log('Error signing out: ', err);
        }

        iam.Token.removeItem('accessToken');
        iam.Token.removeItem('accessTokenExpires');
        const url = (iam.settings.signInPage) + (window.sessionStorage.getItem('redirect_uri') ? '?redirect_uri=' + window.sessionStorage.getItem('redirect_uri') : '/');
        if (iam.settings.debug) console.log('Redirecting back to email page: ' + url);

        iam.Token.setLocation(url);
      }).catch(cb);
    }
  }

  public localizationCallbacks: { callback: () => void, unregisterOnContextTransition: boolean }[] = [];

  /**
   * Provides a mechanism for adding additional localization functionality, after some sort of event, so that changing
   * the language takes the state change into account.
   *
   * @param callback a function which performs localization (see iamLogin.localizeElement())
   * @param localizeNow the callback will be called immediately (in addition to being registered to be called in the future),
   *        if true. defaults to true.
   * @param unregisterOnContextTransition the callback will automatically be unregistered when a widget transition occurs
   *        if true. defaults to false.
   */
  public registerLocalizationCallback(callback: () => void, localizeNow?: boolean, unregisterOnContextTransition?: boolean) {
    if (!this.localizationCallbacks.find(registration => registration.callback === callback)) {
      this.localizationCallbacks.push({
        callback,
        unregisterOnContextTransition: !!unregisterOnContextTransition
      });
      if (localizeNow || typeof localizeNow === 'undefined') {
        callback();
      }
    }
    return callback;
  }

  /**
   * Unregisters previously registered localization functionality if a change of state means it should no longer be
   * executed on language change.
   *
   * @param callback the previously registered callback
   */
  public unregisterLocalizationCallback(callback: () => void) {
    this.localizationCallbacks = this.localizationCallbacks.filter(registration => registration.callback !== callback);
  }

  /**
   * Simplifies localizing an element or attribute in the dom.
   *
   * @param element an element object or an element's id
   * @param options either a string key or an object containing one or more of the following options:<br>
   *     stringKey: the string to retrieve from the translation file<br>
   *     string: in special circumstances like doing locale token substitution in a url, the string might not be localizable, in which case this should be used instead of stringKey<br>
   *     stringParameters: token substitution values to pass into the localization engine (key/value object)<br>
   *     localeSubstitution: boolean to indicate if the resulting string should replace {locale} with the chosen user locale<br>
   *     localeSeparator: when doing locale substitution, this specifies the separator between language and country (defaults to '_')<br>
   *     attribute: localize the specified attribute rather than the element's innerHTML<br>
   *     rawHTML: boolean to indicate if the string shouldn't be escaped<br>
   *     isWidgetElement: boolean to indicate if localizing the element should only occur if the widget is initialized (when the element will exist)
   */
  public localizeElement(element: Element | string, options: string | LocalizeElementOptions) {
    if (typeof options === 'string') {
      options = {
        stringKey: options
      };
    }
    if (!options.isWidgetElement || this.pageInitialized) {
      if (typeof element === 'string') {
        element = document.getElementById(element);
      }
      if (!options.stringKey && !options.string) {
        console.warn('String or string key not specified for localizing element', element);
        return;
      }
      let localized = (options.stringKey ? iamLocalization.localize(options.stringKey, options.stringParameters) : options.string) || '';
      if (options.localeSubstitution) {
        localized = this.localeSubstitution(localized, options.localeSeparator);
      }
      // IAM-5581 - Names containing Apostrophes are not displayed correctly on IAM dashboard:
      // default for escapeValue has been set to true, so default to false so strings with html characters,
      // like quotes, won't be escaped. in none of the 4 cases below do we use unescaped strings unsafely,
      // except potentially the rawHTML case, but in that case the caller is explicitly requesting it.
      if (options.attribute) {
        element.setAttribute(options.attribute, localized);
      } else if (element instanceof HTMLInputElement) {
        element.value = localized;
      } else if (options.rawHTML) {
        element.innerHTML = localized;
      } else {
        element.textContent = localized;
      }
    }
  }

  private hasLocaleToken(string: string) {
    return string.indexOf('{locale}') > -1;
  }

  public localeSubstitution(string: string, separator?: string, preserveCase?: boolean) {
    // since this gets injected into the DOM, escape it to prevent cross site scripting
    let locale = iamUtils.escapeHtml(iamLocalization.getLocale()).replace(/_/g, separator || '_');
    if (!preserveCase) {
      locale = locale.toLowerCase();
    }
    return string.split('{locale}').join(locale);
  }

  /**
   * This function is called ONLY for appending the #usePKCE= value to links from the IAM dashboard.
   * The purpose of this flag is to set the localStorage iam:usePKCE value to the same as the IAM dashboard.
   * The purpose is NOT to set the localStorage value to whatever the current window.iam.settings.usePKCE value is: this value could be different based upon defualt.
   * The purpose is ONLY to set explicitly the localStorage value to be the same localStorage value that was explicitly set in IAM dashboard.
   * Please do NOT change this to use window.iam.settings.usePKCE under ANY circumstances. The ramifications will cause issues with any 'defaulting' we do.
   * @returns the value of iam:usePKCE as '#usePKCE=' if it is set in localStorage, which is passed on to links from the IAM dashboard.
   */
  private getUsePKCEFlagPassOn() {
    const retVal: { [key: string]: string } = {};
    const usePKCEValue = window.localStorage.getItem(iam.consts.storageKeys.usePKCE);
    if (usePKCEValue) {
      retVal[iam.consts.hashKeys.usePKCE] = usePKCEValue;
    }
    return retVal;
  }

  private getTenantIdFlagPassOn() {
    const retVal: { [key: string]: string } = {};
    const tenantId = iam.Token.getCookie(iam.consts?.storageKeys.tId) || iam.Token.getItem(iam.consts?.storageKeys.tId);
    if (tenantId) {
      retVal[iam.consts?.hashKeys.tId] = tenantId;
    }
    return retVal;
  }

  public getFlagPassOn() {
    const flagPassOn = [
      this.getUsePKCEFlagPassOn(),
      this.getTenantIdFlagPassOn()
    ].reduce((obj, passOn) => ({ ...obj, ...passOn }), {} as { [key: string]: string });
    if (Object.keys(flagPassOn).length > 0) {
      return `#${Object.keys(flagPassOn).map(flag => flag + '=' + flagPassOn[flag]).join('&')}`;
    }
    return '';
  }

  /**
   * the "onload" function for every page
   */
  public initializePage = async () => {
    if (!iam.settings.displaySignup) {
      document.getElementById("dont-have-a-mvision").style.display = 'none';
    }

    if (this.isEmailPage()) {
      this.testStorage(true);
    }
    const localeSpecified = this.parseQuery(iam.Token.getLocation().search).locale;
    if (localeSpecified) {
      this.setCookie('iam_user_locale', localeSpecified.replace('-', '_'));
    }

    if (this.isEmailPage()) {
      this.page = new EmailPage();
    } else if (this.isLoginPage()) {
      this.page = new LoginPage();
    } else if (this.isSignInHelpPage()) {
      this.page = new SignInHelpPage();
    } else if (this.isDashboardPage()) {
      this.page = new DashboardPage();
    } else if (this.isUpdatePasswordPage()) {
      this.page = new UpdatePasswordPage();
    } else if (this.isAlreadySignedInPage()) {
      this.page = new IsAlreadySignedInPage();
    } else {
      console.error('unrecognized page address', iam.Token.getLocation().pathname);
    }

    // initialize localization for the page so that localizable strings will be translated now, and any time the language is changed
    await iamLocalization.loadLocalization();
    this.page?.localize();

    this.processNotifications();

    if (iam.settings.siteMigrationRedirect && iam.settings.siteMigrationRedirect !== iam.settings.signInPage) {
      if (!this.isDashboardPage()) {
        this.addLangOptions();
      }
      document.getElementById('dont-have-a-mvision').style.display = 'none';
      document.querySelector<HTMLElement>('.rightBody').style.display = 'none';
      document.getElementById('siteMigrationRedirectMessage').style.display = 'block';
      document.getElementById('loading-content').style.display = 'none';
      document.getElementById('right-pane-contents').style.display = '';
      this.registerLocalizationCallback(() => {
        this.localizeElement('redirectText', { stringKey: 'migrationRedirect', stringParameters: { newSite: iam.settings.siteMigrationRedirect, company: iam.settings.branding === 'skyhigh' ? 'Skyhigh Security' : 'Trellix' }, rawHTML: true });
        this.localizeElement('redirectButton', 'redirectNow');
      });
      const redirect = function () {
        iam.Token.setLocation(iam.Token.getLocation().href.split(iam.settings.signInPage).join(iam.settings.siteMigrationRedirect)); // replace the origin wherever it appears in the url
      };
      document.getElementById('redirectButton').addEventListener('click', redirect);
      setTimeout(redirect, 15000);
    } else {
      this.page?.initialize();
    }
  };

  private testStorage(displayError: boolean): void {
    try {
      window.localStorage.setItem('storagetest', 'success');
      this.storageEnabled = window.localStorage.getItem('storagetest') === 'success';
      window.localStorage.removeItem('storagetest');
    } catch (e) {
      this.storageEnabled = false;
    }
    if (displayError && !this.storageEnabled) {
      this.showErrorMessageLocalizable('cookiesRequired');
    }
  }

  public sessionIsActive() {
    return !!iam.Token.getItem('accessToken');
  }

  public signInBack(event: Event) {
    // take the user back to the email page
    this.showTransitioningWidgetPage();
    if (this.page.elements.getSubmitButton()) {
      this.page.elements.getSubmitButton().disabled = true;
    }
    this.redirectBackToEmail();
    event.stopImmediatePropagation();
    return false;
  }

  public showWidget() {
    document.getElementById('loading-content').style.display = 'none';
    this.page.elements.getContainer().style.display = '';
  }

  public unshowTransitioningWidgetPage() {
    this.page.elements.getContainer().querySelectorAll('input,select').forEach(function (control) {
      control.removeAttribute('readonly');
    });
    if (this.page.elements.getSubmitButton()) {
      this.page.elements.getSubmitButton().disabled = false;
    }
  }

  public showTransitioningWidgetPage() {
    this.page.elements.getContainer().querySelectorAll('input,select').forEach(function (control) {
      control.setAttribute('readonly', 'true');
    });
    if (this.page.elements.getSubmitButton()) {
      this.page.elements.getSubmitButton().disabled = true;
    }
  }

  public isEmailAddressValid(email: string) {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email) && email.length > 0;
  }

  public setEmailFormValue(value: string) {
    (document.getElementById('login_hint') as HTMLInputElement).value = value;
    this.page.validateForm();
  }

  public getEmailFormValue() {
    return (document.getElementById('login_hint') as HTMLInputElement).value;
  }

  public getEmailFromToken() {
    const token = iam.Token.getItem('accessToken');
    if (token) {
      const parsedToken = iam.Token.parseToken(token);
      return parsedToken.login_hint || '';
    } else {
      return '';
    }
  }

  private createBanner(isError: boolean) {
    const divBanner = document.createElement('div');
    divBanner.className = 'msg-banner ' + (isError ? 'errorBanner' : 'infoBanner');

    const bannerImage = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    bannerImage.setAttributeNS(null, 'class', 'lsg-icon-component');
    const bannerImageUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    bannerImageUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', isError ? 'images/lsg-sprites.svg#alert-solid' : 'images/lsg-sprites.svg#allow-solid');
    bannerImage.appendChild(bannerImageUse);
    divBanner.appendChild(bannerImage);

    divBanner.appendChild(document.createElement('div')); // message div

    const closeImage = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    closeImage.setAttributeNS(null, 'class', 'lsg-icon-component');
    const closeImageUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    closeImageUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', 'images/lsg-sprites.svg#close-max');
    closeImage.appendChild(closeImageUse);
    closeImage.addEventListener('click', () => {
      this.hideMessageBanners(divBanner);
    });
    divBanner.appendChild(closeImage);

    return divBanner;
  }

  private bannerMessageLocalizableCallbacks: { banner: HTMLDivElement, callback: () => void }[] = [];

  private errorMessageTime: number = null;

  public resetErrorMessageTime() {
    this.errorMessageTime = null;
  }

  public howLongSinceErrorDisplayed() {
    return this.errorMessageTime ? (new Date()).getTime() - this.errorMessageTime : 0;
  }

  /**
   * Show a pre-localized error message in a banner at the top.
   *
   * @param string a pre-localized error message
   */
  public showErrorMessage(string: string) {
    // pre-localized - no need to do a localization callback
    const banner = this.createBanner(true);
    document.getElementById('message-container').appendChild(banner);
    banner.querySelector('div').textContent = iamUtils.escapeHtml(string);
    this.errorMessageTime = (new Date()).getTime();
    return banner;
  }

  /**
   * Show "unexpected error" message with error code in a banner at the top.
   *
   * @param errorCode defined in ErrorCodes, a word which is meaningless to the customer but identifies the problem to support - using names of trees
   */
  public showUnexpectedErrorMessage(errorCode: string) {
    return this.showErrorMessageLocalizable('unexpectedError', { code: errorCode });
  }

  /**
   * Show localizable error message in a banner at the top.
   *
   * @param stringKey string key for the desired error message
   * @param stringParameters an optional set of strings for token substitution
   */
  public showErrorMessageLocalizable(stringKey: string, stringParameters?: LocalizationParameters) {
    // use a localization callback to localize this error message so it updates if the user changes the language
    const banner = this.createBanner(true);
    document.getElementById('message-container').appendChild(banner);
    this.bannerMessageLocalizableCallbacks.push({
      banner,
      callback: this.registerLocalizationCallback(() => {
        // set the error message string
        this.localizeElement(banner.querySelector('div'), { stringKey, stringParameters });
      })
    });
    this.errorMessageTime = (new Date()).getTime();
    return banner;
  }

  /**
   * Show localizable info message in a banner at the top.
   *
   * @param stringKey string key for the desired info message
   * @param stringParameters an optional set of strings for token substitution
   */
  public showInfoMessageLocalizable(stringKey: string, stringParameters?: LocalizationParameters) {
    // use a localization callback to localize this info message so it updates if the user changes the language
    const banner = this.createBanner(false);
    document.getElementById('message-container').appendChild(banner);
    this.bannerMessageLocalizableCallbacks.push({
      banner,
      callback: this.registerLocalizationCallback(() => {
        // set the error message string
        this.localizeElement(banner.querySelector('div'), { stringKey, stringParameters });
      })
    });
    return banner;
  }

  /**
   * Hide the message banners at the top of the page
   *
   * @param optionalBanner dom element for the individual message banner if just a single message is to be hidden
   */
  public hideMessageBanners(optionalBanner?: Element) {
    const messageContainer = document.getElementById('message-container');
    // remove messages and unregister message callbacks
    if (optionalBanner) {
      messageContainer.removeChild(optionalBanner);
      this.bannerMessageLocalizableCallbacks = this.bannerMessageLocalizableCallbacks.filter(registeredcb => {
        if (registeredcb.banner === optionalBanner) {
          this.unregisterLocalizationCallback(registeredcb.callback);
          return false;
        } else {
          return true;
        }
      });
    } else {
      while (messageContainer.firstChild) {
        messageContainer.removeChild(messageContainer.firstChild);
      }
      this.bannerMessageLocalizableCallbacks.forEach(registeredcb => {
        this.unregisterLocalizationCallback(registeredcb.callback);
      });
      this.bannerMessageLocalizableCallbacks = [];
    }
    this.resetErrorMessageTime();
  }

  public isErrorMessageShown() {
    return document.getElementById('message-container').querySelectorAll('.msg-banner').length > 0;
  }

  public filterNotifications(notifications: IamNotification[], banner: boolean, consolesToMatch?: string[]) {
    const now = iamUtils.now();
    return notifications.filter(notification => {
      if ((banner && notification.priority > 0) || (!banner && notification.priority <= 0)) { // banner messages are priority 0
        return false;
      } else if (notification.start && new Date(notification.start) >= now) {
        // start date is in the future
        return false;
      } else if (notification.end && new Date(notification.end) < now) {
        // end date is in the past
        return false;
      } else if (consolesToMatch && !notification.consoles) {
        // looking for matching consoles and we've already retrieved the user's list of console apps but this notification doesn't specify any
        return false;
      } else if (consolesToMatch?.length && notification.consoles?.length && !notification.consoles.some(lookingFor => consolesToMatch.some(has => lookingFor === has))) {
        // looking for matching consoles and we've already retrieved the user's list of console apps but the user doesn't have any of the ones specified in the notification
        return false;
      } else if (consolesToMatch && !consolesToMatch.length && notification.consoles?.length) {
        // looking for matching consoles but we haven't retrieved the user's list of console apps yet
        return false;
      } else if (!consolesToMatch && notification.consoles?.length) {
        // not looking for matching consoles but this is a console-specific notification
        return false;
      } else {
        return true;
      }
    });
  }

  public getNotificationsPromise() {
    return new Promise<{ messages: IamNotification[] }>(resolve => {
      const xmlhttp = new XMLHttpRequest();
      xmlhttp.onreadystatechange = () => {
        if (xmlhttp.readyState === XMLHttpRequest.DONE) {
          if (xmlhttp.status === 200) {
            if (iam.settings.debug) console.log('XMLHttpRequest', xmlhttp);
            if (xmlhttp.responseText) {
              try {
                resolve(JSON.parse(xmlhttp.responseText));
              } catch (err) {
                console.error(err);
                resolve(null);
              }
            } else {
              resolve(null);
            }
          } else if (xmlhttp.status === 400) {
            console.log('There was an error 400', xmlhttp);
            resolve(null);
          } else {
            console.log('something else other than 200 was returned ', xmlhttp);
            resolve(null);
          }
        }
      };
      xmlhttp.open('GET', this.localeSubstitution(iam.settings.notificationJson, '_', true), true);
      xmlhttp.send();
    });
  }

  /**
   * If there are any notifications (e.g. scheduled maintenance), they will be displayed in a dropdown at the top
   */
  public processNotifications() {
    const thirdPartyCookieNotification = (): IamNotification => {
      if (this.isSafari()) {
        return {
          title: iamLocalization.localize('safariDetected'),
          priority: 3,
          text: iamLocalization.localize('safariCrossSite')
        };
      }
    };

    function getMin (arr: IamNotification[], prop: keyof IamNotification) {
      let min: IamNotification;
      for (let i = 0; i < arr.length; i++) {
        if (!min || parseInt(String(arr[i][prop])) < parseInt(String(min[prop]))) {
          min = arr[i];
        }
      }
      return min;
    }

    function createNotificationDiv (message: IamNotification) {
      // build an individual notification div for the notifications popup
      const notificationDiv = document.createElement('div');
      switch (message.priority) {
        case 4: notificationDiv.className = 'notification statusSuccess'; break;
        case 3: notificationDiv.className = 'notification statusInformational'; break;
        case 2: notificationDiv.className = 'notification statusNotification'; break;
        default: notificationDiv.className = 'notification statusCritical'; break;
      }

      const headingDiv = document.createElement('div');
      headingDiv.className = 'notificationHeading';

      const notificationImage = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      notificationImage.setAttributeNS(null, 'class', 'lsg-icon-component status');
      const notificationImageUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      switch (message.priority) {
        case 4: notificationImageUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', 'images/lsg-sprites.svg#allow-solid'); break;
        case 3: notificationImageUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', 'images/lsg-sprites.svg#information-solid'); break;
        case 2: notificationImageUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', 'images/lsg-sprites.svg#alert-solid'); break;
        default: notificationImageUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', 'images/lsg-sprites.svg#alert-solid'); break;
      }
      notificationImage.appendChild(notificationImageUse);

      const headingTextDiv = document.createElement('div');
      const titleSpan = document.createElement('span');
      titleSpan.className = 'notificationTitle';
      headingTextDiv.appendChild(titleSpan);
      titleSpan.appendChild(document.createTextNode(message.title));
      headingTextDiv.appendChild(document.createElement('br'));

      if (message.subtitle) {
        const subtitleSpan = document.createElement('span');
        subtitleSpan.appendChild(document.createTextNode(message.subtitle));
        headingTextDiv.appendChild(subtitleSpan);
      }

      const messageDiv = document.createElement('div');
      messageDiv.className = 'notificationMessage';
      messageDiv.innerHTML = message.text;

      headingDiv.appendChild(notificationImage);
      headingDiv.appendChild(headingTextDiv);
      notificationDiv.appendChild(headingDiv);
      notificationDiv.appendChild(messageDiv);

      document.getElementById('notificationPopup').appendChild(notificationDiv);
    }

    const fetchNotifications = () => {
      document.getElementById('notificationPopup').innerHTML = '';

      // fetch notifications
      this.getNotificationsPromise().then(jsonResponse => {
        let oldStyleNotifications: IamNotification[];
        if (jsonResponse) {
          if (iam.settings.debug) console.log('jsonResponse', jsonResponse);
          oldStyleNotifications = this.filterNotifications(jsonResponse.messages, false);
          const nonConsoleSpecificBanners = this.filterNotifications(jsonResponse.messages, true);
          let consoleSpecificBanners;
          if (this.isDashboardPage()) {
            consoleSpecificBanners = this.filterNotifications(jsonResponse.messages, true, (JSON.parse(window.localStorage.getItem('iam:consoleApps')) as ConsoleApp[] || []).map(x => x.name));
          }
          let bannerNotificationText;
          if (consoleSpecificBanners?.length > 0) {
            bannerNotificationText = consoleSpecificBanners[0].text;
          } else if (nonConsoleSpecificBanners.length > 0) {
            bannerNotificationText = nonConsoleSpecificBanners[0].text;
          }
          if (bannerNotificationText) {
            document.getElementById('banner').innerHTML = bannerNotificationText;
            document.getElementById('banner').style.display = 'block';
          } else {
            document.getElementById('banner').style.display = 'none';
          }
        } else {
          oldStyleNotifications = [];
        }

        if (!iam.settings.useCookies) {
          const thirdPartyCookieNotificationMessage = thirdPartyCookieNotification();
          if (thirdPartyCookieNotificationMessage) {
            oldStyleNotifications.push(thirdPartyCookieNotificationMessage);
          }
        }

        if (!oldStyleNotifications || oldStyleNotifications.length === 0) {
          document.getElementById('notificationControl').style.display = 'none';
          return;
        }

        if (iam.settings.debug) console.log('oldStyleNotifications', oldStyleNotifications);
        const maxPriority = getMin(oldStyleNotifications, 'priority');
        if (iam.settings.debug) console.log('maxPriority', maxPriority.priority);
        const localizeNotificationNumber = () => {
          this.localizeElement('notificationNumber', {
            stringKey: oldStyleNotifications.length === 1 ? 'oneNotification' : 'multipleNotifications',
            stringParameters: { count: oldStyleNotifications.length }
          });
        };
        if (this.hasLocaleToken(iam.settings.notificationJson)) {
          // it's already in an outer localization callback
          localizeNotificationNumber();
        } else {
          // it's not in an outer localization callback
          this.registerLocalizationCallback(localizeNotificationNumber);
        }

        let className;
        switch (maxPriority.priority) {
          case 4: className = 'statusSuccess'; break;
          case 3: className = 'statusInformational'; break;
          case 2: className = 'statusNotification'; break;
          default: className = 'statusCritical'; break;
        }
        document.getElementById('notificationControl').className = 'notifications ' + className;
        document.getElementById('notificationControl').style.display = '';

        for (let i = 0; i < oldStyleNotifications.length; i++) {
          createNotificationDiv(oldStyleNotifications[i]);
        }
      });
    }
    if (iam.settings.notificationJson) {
      if (this.hasLocaleToken(iam.settings.notificationJson)) {
        this.registerLocalizationCallback(fetchNotifications);
      } else {
        fetchNotifications();
      }
    }
  }

  public handleExchangeToken(accessToken: string, tenantId: string) {
    if (iam.settings.debug) console.log('Exchanging token');
    const xmlhttp = new XMLHttpRequest();

    xmlhttp.onreadystatechange = () => {
      if (xmlhttp.readyState === XMLHttpRequest.DONE) {
        if (xmlhttp.status === 200) {
          if (iam.settings.debug) console.log('XMLHttpRequest', xmlhttp);

          if (!xmlhttp.responseText) {
            return;
          }

          const jsonResponse = JSON.parse(xmlhttp.responseText);
          this.setToken(jsonResponse.accessToken, jsonResponse.expiresIn);
        } else {
          console.log('something else other than 200 was returned ', xmlhttp);
          iamLocalization.loadLocalization().then(() => {
            iam.Token.setLocation(iam.settings.discovery + '?error=' + encodeURIComponent(iamLocalization.localize('errorSendingRequest')));
          });
        }
      }
    };
    xmlhttp.open('GET', iam.settings.uamBackendURL + '/token?tenant_id=' + tenantId, true);
    xmlhttp.setRequestHeader('Authorization', 'Bearer ' + accessToken);
    xmlhttp.send();
  }

  public getWhiteListURLPromise(accessToken: string): Promise<string> {
    return new Promise(function (resolve, reject) {
      // TPS is optional. When disabled, skip the TPS call and let permittedURL fall back to fallbackWhitelistURL
      // Doing `=== false` so if the useTPS setting is undefined, it will still call TPS (for backward compat.)
      if (iam.settings.useTPS === false) {
        if (iam.settings.debug) console.log('useTPS disabled — skipping TPS whitelist fetch');
        resolve('done');
        return;
      }
      if (iam.settings.debug) console.log('Get Whitelist URL from TPS');
      const whitelistRequest = new XMLHttpRequest();
      whitelistRequest.onreadystatechange = function () {
        if (whitelistRequest.readyState === XMLHttpRequest.DONE) {
          if (whitelistRequest.status === 200) {
            if (iam.settings.debug) console.log('XMLHttpRequest', whitelistRequest);
            if (whitelistRequest.responseText) {
              let jsonResponse = JSON.parse(whitelistRequest.responseText);
              if (iam.settings.debug) console.log('response from tps', JSON.stringify(jsonResponse));
              jsonResponse = jsonResponse.concat(iam.settings.fallbackWhitelistURL);
              if (iam.settings.debug) console.log('include fallback whitelist ', JSON.stringify(jsonResponse));
              iam.Token.setItem('iam_tenant_whitelisturl', JSON.stringify(jsonResponse));
            }
          } else if (whitelistRequest.status === 404) {
            console.log('There was an error 404', whitelistRequest);
          } else {
            console.log('something else other than 200 was returned ', whitelistRequest.responseText);
          }
          resolve('done');
        }
      };
      whitelistRequest.open('GET', iam.settings.tpsURL + 'whitelisturl', true);
      whitelistRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
      whitelistRequest.send();
    });
  }

  public validateRedirectUri(uri: string) {
    let redirectUri = iam.settings.appURL;
    if (uri && iam.Token.permittedURL(uri)) {
      redirectUri = uri;
    }
    return redirectUri;
  }

  public setToken(accessToken: string, expiresIn: number | string) {
    if (!iam.settings.usePKCE) {
      if (iam.settings.debug) console.log('Setting accessToken: ' + accessToken);
      const expiresAt = new Date(Number(expiresIn) * 1000 + new Date().getTime());
      if (iam.settings.debug) console.log('accessToken expires at: ' + expiresAt);

      iam.Token.setItem('accessToken', accessToken);
      iam.Token.setItem('accessTokenExpires', expiresAt);
    }
    if (self === top) {
      this.redirectToApp();
    } else {
      iam.Token.scheduleRenewal();
    }
  }

  // Handles the access token in the URL for implicit flow
  // Handles the code for PKCE flow.
  public handleAuthentication = async () => {
    if (iam.settings.debug) console.log('Check for Tokens in URL (fragment): ', iam.Token.getLocation().hash);
    let params: { [key: string]: string } = {};
    if (iam.Token.getLocation().hash.length > 1) {
      params = iam.Token.getHashParams(iam.Token.getLocation().hash);
      if (iam.settings.debug) console.log(params);
    }
    if (params && (params.error || params.error_description)) { // AuthZ error case
      iam.Token.setLocation(iam.settings.discovery + iam.Token.getLocation().hash);
    } else if (params && params.access_token) { // Implicit (need to check PKCE partners)
      if (window.localStorage.getItem('t_id')) {
        const tenantID = window.localStorage.getItem('t_id');
        // window.localStorage.removeItem('t_id'); Partner user refresh token workflow
        this.handleExchangeToken(params.access_token, tenantID);
      } else {
        this.setToken(params.access_token, params.expires_in);
      }
    } else if (iam.settings.usePKCE) {
      // PKCE case
      if (iam.settings.debug) console.log('using PKCE for handleAuthentication');
      if (window.localStorage.getItem(iam.consts?.hashKeys.tId)) {
        this.setCookie(iam.consts?.storageKeys.tId, window.localStorage.getItem(iam.consts?.hashKeys.tId));
      }
      const accessToken = iam.Token.getItem('accessToken');
      const urlParams = new URLSearchParams(window.location.search);
      const pkceCode = urlParams.get('code');
      // check for code in the url for tenant switch case.
      if (pkceCode || !accessToken) {
        // i dont have an access token OR i have PKCE code, trade for token
        await iam.Token.getTokenPromise({ pkceRedirectUrl: iam.settings.tokenHandler, init: true });
      }
      this.redirectToApp();
    } else {
      // Implicit
      iam.Token.scheduleRenewal();
    }
  };

  public processConsolesResult(xhr: XMLHttpRequest, resolve: (apps: ConsoleApp[]) => void, reject: (error: Error) => void) {
    if (xhr.readyState === XMLHttpRequest.DONE) {
      if (xhr.status === 200) {
        try {
          if (iam.settings.debug) console.log(xhr.responseText);
          resolve(JSON.parse(xhr.responseText));
        } catch (error) {
          console.log('console api error=' + error);
          reject(this.createMethodErr('getConsoles', error));
        }
      } else {
        console.log('console api status error=' + xhr.status);
        reject(this.createMethodErr('getConsoles', xhr.responseText));
      }
    }
  }

  public getConsoles(token: string) {
    return new Promise<ConsoleApp[]>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onreadystatechange = () => {
        this.processConsolesResult(xhr, resolve, reject);
      };
      xhr.open('GET', iam.settings.consoleURL + '?console=iam', true);
      xhr.setRequestHeader('Authorization', 'Bearer ' + token);
      xhr.send();
    });
  }

  public getEpoResolver(tenantId: string) {
    return new Promise<{ partition: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          if (xhr.status === 200) {
            try {
              if (iam.settings.debug) console.log(xhr.responseText);
              resolve(JSON.parse(xhr.responseText));
            } catch (error) {
              console.log('getEpoResolver api error= ' + error);
              reject(this.createMethodErr('getEpoResolver', error));
            }
          } else {
            console.log('getEpoResolver api status error = ' + xhr.status);
            reject(this.createMethodErr('getEpoResolver', xhr.responseText));
          }
        }
      };
      xhr.open('GET', iam.settings.tpsResolverURL + tenantId, true);
      xhr.send();
    });
  }

  public getEpoRedirectUrl = async () => {
    // TPS is optional. When disabled, skip the ePO resolver call and use the default appURL
    // Doing `=== false` so if the useTPS setting is undefined, it will still call TPS (for backward compat.)
    if (iam.settings.useTPS === false) {
      if (iam.settings.debug) console.log('useTPS disabled — skipping ePO resolver');
      return iam.settings.appURL;
    }
    const token = window.localStorage.getItem('accessToken');
    const parsedToken = await iam.Token.parseToken(token);
    const tenantId = parsedToken.tenant_id;
    try {
      const epoResolver = await this.getEpoResolver(tenantId);
      return epoResolver.partition + iam.settings.epoPartitionPath;
    } catch (error) {
      return iam.settings.appURL;
    }
  };

  private processRedirectToApp = async (token: string, redirectUri: string, initiator: string) => {
    if (token && this.appendTokenUrl(redirectUri)) {
      redirectUri = await this.getEpoRedirectUrl();
    } else if (iam.settings.usePKCE && initiator === 'mvisionsso') {
      const accessToken = iam.Token.getItem('accessToken');
      const accessTokenExpires = iam.Token.getItem('accessTokenExpires');
      const refreshToken = iam.Token.getItem('refreshToken');
      redirectUri = `${redirectUri}#access_token=${accessToken}&accessTokenExpires=${accessTokenExpires}&initiator=${initiator}&refreshToken=${refreshToken}`;
    }
    if (iam.settings.debug) console.log('Redirecting to... ' + redirectUri);
    iam.Token.setLocation(redirectUri);
  };

  // called on the auth.html page during login to determine whether to go to the requested app or force the user to the dashboard instead
  public forceRedirectToDashboard = (redirectUri: string, consoleApps: ConsoleApp[], notifications: IamNotification[]) => {
    const originFromURL = (url: string) => {
      try {
        return new URL(url).origin;
      } catch {
        return null;
      }
    };
    // in case the redirectURI's and app URL's paths don't match, just base it on the origin part (https://foo.bar) since they should be unique per app
    const redirectUriOrigin = originFromURL(redirectUri);
    const matchingApps = consoleApps.filter(app => originFromURL(app.url) === redirectUriOrigin);
    // does the app matching the redirectURI have a corresponding app-specific notification that needs to be shown before the user navigates to the app?
    return matchingApps.some(app => this.filterNotifications(notifications, true, [app.name]).length > 0);
  };

  // Redirect to the Dashboard app
  public redirectToApp() {
    let redirectUri = window.sessionStorage.getItem('redirect_uri') || iam.settings.customPostLoginRedirect || iam.settings.appURL;
    window.sessionStorage.removeItem('redirect_uri');
    window.sessionStorage.removeItem('persistent_redirect_uri');
    window.localStorage.removeItem('iam:consoleApps');
    const initiator = window.sessionStorage.getItem('initiator');
    window.sessionStorage.removeItem('initiator');
    const token = iam.Token.getItem('accessToken');
    const parsedToken = iam.Token.parseToken(token);
    const loginHint = parsedToken.login_hint;
    let numTenants = 0;
    let consoleApps: ConsoleApp[] = null;

    const whitelistPromise = this.getWhiteListURLPromise(token).then(() => {
      redirectUri = this.validateRedirectUri(redirectUri);
    });

    const consolesPromise = this.getConsoles(token).then(function (apps) {
      consoleApps = apps;
    }).catch(function (error) {
      if (error instanceof Error) {
        return error;
      } else {
        return new Error(error);
      }
    });

    const tenantDetailsPromise = iam.Token.getTenantDetails(loginHint).then(function (tenants) {
      if (iam.settings.debug) console.log('setting tenants');
      window.localStorage.setItem('tenants', JSON.stringify(tenants));
      numTenants = tenants.length;
    }).catch(function (error) {
      if (error instanceof Error) {
        return error;
      } else {
        return new Error(error);
      }
    });

    // get notifications if the dashboard isn't our current destination, to determine if we need to go there anyway to display a console-specific notification
    // (in case this looks odd, when promise is null, Promise.all will immediately resolve it with a result of null)
    const notificationsPromise = redirectUri !== iam.settings.appURL ? this.getNotificationsPromise() : null;

    return Promise.all([whitelistPromise, consolesPromise, tenantDetailsPromise, notificationsPromise]).then(async results => {
      window.localStorage.removeItem('iam:consoleApps'); // set the consoleApps to empty, it will be changed later if we were successful.
      // do we need to force redirect to the dashboard to show a notification for the redirect app?
      let forcingToDashboard = false;
      if (results[3] && this.forceRedirectToDashboard(redirectUri, consoleApps, results[3].messages)) {
        // there is a banner notification for the app the user is logging into, so rather than redirect to the app, redirect to the dashboard so the user can see the notification
        forcingToDashboard = true;
        redirectUri = iam.settings.appURL;
      }
      // Handle error from getTenantDetails first
      if (results[2] instanceof Error) {
        const error = JSON.parse(results[2].message);
        if (iam.settings.debug) console.log(error.cause);
        window.localStorage.setItem('tenants', JSON.stringify([
          {
            tenantId: parsedToken.tenant_id,
            companyName: parsedToken.tenant_id
          }
        ]));
        if (token && this.appendTokenUrl(redirectUri)) {
          redirectUri = await this.getEpoRedirectUrl();
        }
        if (iam.settings.debug) console.log(`Error in getTenantDetails. Redirecting to ${redirectUri}`);
        iam.Token.setLocation(redirectUri);
        // If getConsoles threw an error instead, handle that
      } else if (results[1] instanceof Error) {
        const error = JSON.parse(results[1].message);
        if (iam.settings.debug) console.log(error.cause);
        if (iam.settings.debug) console.log(`Error in getConsoles. Redirecting to ${redirectUri}`);
        this.processRedirectToApp(token, redirectUri, initiator);
        // Otherwise, redirect to app as normal
      } else {
        window.localStorage.setItem('iam:consoleApps', JSON.stringify(consoleApps));
        if (!this.overrideRedirectUri(initiator)) {
          if (iam.settings.debug) console.log(`Initiator is ${initiator}. Redirecting to ${redirectUri}`);
          this.processRedirectToApp(token, redirectUri, initiator);
        } else if (numTenants > 1) {
          redirectUri = iam.settings.appURL;
          if (iam.settings.debug) console.log(`Multitenant case. Redirecting to ${redirectUri}`);
          this.processRedirectToApp(token, redirectUri, initiator);
        } else {
          if (!forcingToDashboard && consoleApps && consoleApps.length === 1 && consoleApps[0].name.replace(' ', '').toLowerCase() === 'mvisioncloud') { // reset only if it is mvision cloud
            redirectUri = consoleApps[0].url;
          }
          if (iam.settings.debug) console.log(`Regular case. Redirecting to ${redirectUri}`);
          this.processRedirectToApp(token, redirectUri, initiator);
        }
      }
    });
  }

  public overrideRedirectUri(initiator: string) {
    if (initiator && ['mvisionsso', 'eur'].includes(initiator)) {
      console.log(initiator);
      return false;
    }
    return true;
  }

  private createMethodErr(method: string, cause: unknown) {
    return new Error(JSON.stringify({
      method,
      cause
    }));
  }

  public appendTokenUrl(uri: string) {
    for (let i = 0; i < iam.settings.appendTokenUrls.length; i++) {
      const entry = iam.settings.appendTokenUrls[i];
      if (uri.indexOf(entry) >= 0) {
        return true;
      }
    }
    return false;
  }

  // Parses the URL for query parameters
  public parseQuery(queryString: string): { [key: string]: string } {
    const query: { [key: string]: string } = {};
    // URLSearchParams will skip past a leading '?' but not a leading '#' so we have to do that manually
    for (const [key, value] of new URLSearchParams(queryString?.startsWith('#') ? queryString.slice(1) : queryString).entries()) {
      query[key] = value;
    }
    return query;
  }

  // display errors passed to email.html
  public handleErrors() {
    if (iam.settings.debug) console.log('Check for Errors in URL (fragment): ', iam.Token.getLocation().hash);
    let params: { [key: string]: string } = {};
    const oktaSignIn = new OktaSignIn({ baseUrl: iam.settings.domain });

    if (iam.Token.getLocation().search && this.parseQuery(iam.Token.getLocation().search)) {
      params = iamLogin.parseQuery(iam.Token.getLocation().search);
      if (iam.settings.debug) console.log(params);
    } else if (iam.Token.getLocation().hash && iamLogin.parseQuery(iam.Token.getLocation().hash)) {
      params = iamLogin.parseQuery(iam.Token.getLocation().hash);
      if (iam.settings.debug) console.log(params);
    }

    if (params.error || params.error_description) {
      if (params.error === 'invalid_request' || params.error === 'invalid_client') {
        if (params.error_description === 'logout endpoint: missing cookie' && OktaSignIn) {
          // sign out and return so we do not display the error
          if (iam.settings.debug) console.log('invalid_request - signing out');
          oktaSignIn.authClient.closeSession().catch(function (err) {
            if (err) {
              if (iam.settings.debug) console.log('invalid_request - signing out error', err);
            }
          });
        } else if (params.error_description?.endsWith('No tenant id available')) {
          this.showErrorMessageLocalizable('noTenantId');
          oktaSignIn.authClient.closeSession().catch(function (err) {
            if (err) {
              if (iam.settings.debug) console.log('signing out error', err);
            }
          });
        } else {
          if (params.error_description) {
            console.error(params.error + ': ' + params.error_description);
          } else {
            console.error(params.error);
          }
          this.showUnexpectedErrorMessage(iamErrorCodes.invalidRequest);
        }
      } else if (params.error?.startsWith('Misconfigured identity provider')) {
        this.showErrorMessageLocalizable('misconfiguredIdp');
      } else {
        const authorized = this.isTrustedReferrer();
        if (params.error) {
          if (authorized) {
            this.showErrorMessage(params.error);
          } else {
            console.error('arbitrary unknown error in url: ' + params.error);
          }
        }
        if (params.error_description) {
          if (authorized) {
            this.showErrorMessage(params.error_description);
          } else {
            console.error('arbitrary unknown error description in url: ' + params.error_description);
          }
        }
        if (!authorized) {
          this.showUnexpectedErrorMessage(iamErrorCodes.unexpectedError);
        }

        // Always sign out on error unless it's the "already signed in" page
        if (!this.isAlreadySignedInPage()) {
          oktaSignIn.authClient.closeSession().catch(function (err) {
            if (err) {
              if (iam.settings.debug) console.log('signing out error', err);
            }
          });
        }
      }
    }
  }

  public isTrustedReferrer(): boolean {
    if (document.referrer) {
      let hostname;
      try {
        hostname = new URL(document.referrer).hostname;
      } catch (e) {
        console.error('referrer is not in URL form ', e);
        return false;
      }
      // not specifically the purpose of fallbackWhitelistURL, but the domains specified there should be a pretty good test for what domains we should allow
      for (const url of iam.settings.fallbackWhitelistURL) {
        if (hostname === url.domain || hostname.endsWith('.' + url.domain)) {
          return true;
        }
      }
    }
    return false;
  }

  // set the cookie
  public setCookie(cookieName: string, cookie: string) {
    // max age is set to 6 month in seconds
    document.cookie = cookieName + '=' + cookie + ';path=/;domain=' +
        iam.settings.sharedCookieDomain + ';max-age=15552000;secure';
  }
  // remove the cookie
  public removeCookie(cookieName: string) {
    // to delete a cookie, you set it with an expiration date in the past
    document.cookie = cookieName + '=;path=/;domain=' +
        iam.settings.sharedCookieDomain + ';expires=Thu, 01 Jan 1970 00:00:01 GMT;secure';
  }

  /**
   * Add langauges to the language dropdown
   */
  public addLangOptions() {
    let langSelected = false;
    const currentUserLocale = iamLocalization.getLocale();
    const langDropDown = document.getElementById('selectLanguage');
    for (let i = 0; i < iam.settings.languageOptions.length; i++) {
      const optn = document.createElement('li');
      langDropDown.children[0].appendChild(optn);
      const a = document.createElement('a');
      /* jshint -W107 */ // temporarily ignore jslint's warning about a script url
      a.href = 'javascript:;'; // better than setting it to '#' because okta widget watches hashchange events and it messes up the state of the widget
      /* jshint +W107 */
      a.textContent = iam.settings.languageOptions[i].countryLang;
      a.dataset.value = iam.settings.languageOptions[i].langCode;
      a.tabIndex = -1;
      a.addEventListener('click', function (e: MouseEvent) {
        // user selected a language
        langDropDown.className = '';
        langDropDown.dataset.value = this.dataset.value;
        iamLogin.setCookie('iam_user_locale', this.dataset.value);
        document.getElementById('selectLang').textContent = this.textContent;
        if (e.isTrusted) { // "trusted" means a real user click, not a programmatic .click() call
          // force the popup away momentarily since hovering over the popup otherwise keeps it visible. I was using a timer
          // but that doesn't work in IE or Edge because the ":hover" css selector doesn't clear until the mouse actually
          // moves, so if you select a language and don't move the mouse, the menu would just reappear.
          langDropDown.style.display = 'none';
          const leave = function () {
            langDropDown.style.display = '';
            langDropDown.removeEventListener('mouseleave', leave);
          };
          langDropDown.addEventListener('mouseleave', leave);
        } // else initiated by .click() from the keyboard event below
        // re-translate all localizable strings
        iamLocalization.translate();
        iamLogin.page.localeChanged(this.dataset.value);
        e.stopImmediatePropagation();
        return false;
      });
      if (currentUserLocale === a.dataset.value) {
        langDropDown.dataset.value = a.dataset.value;
        document.getElementById('selectLang').textContent = a.textContent;
        langSelected = true;
      }
      optn.appendChild(a);
    }
    if (!langSelected) {
      // whatever the current locale is (or isn't), it's not in the list, so default to english
      this.setCookie('iam_user_locale', 'en_US');
      document.getElementById('selectLang').textContent = 'English';
    }

    const list = langDropDown.querySelector('ul');
    const navigate = ($event: KeyboardEvent, nextChooser: (index: number) => number) => {
      const prev = list.querySelector('.focused');
      let index;
      if (prev) {
        index = Array.from(list.children).indexOf(prev);
        if (index < 0) {
          index = list.children.length;
        }
      } else {
        index = list.children.length;
      }
      index = Math.min(Math.max(0, nextChooser(index)), list.children.length - 1);
      prev?.classList.remove('focused');
      list.children[index].classList.add('focused');
      $event.preventDefault();
      $event.stopPropagation();
    };
    document.getElementById('language').addEventListener('keydown', ($event: KeyboardEvent) => {
      const key = $event.key;
      switch (key) {
        case 'ArrowUp':
          navigate($event, index => index - 1);
          break;
        case 'ArrowDown':
          navigate($event, index => index + 1);
          break;
        case 'Home':
          navigate($event, () => 0);
          break;
        case 'End':
          navigate($event, () => list.children.length - 1);
          break;
        case 'Enter':
          list.querySelector('.focused')?.querySelector('a').click();
          $event.preventDefault();
          $event.stopPropagation();
      }
    });
    document.getElementById('language').addEventListener('blur', () => {
      list.querySelector('.focused')?.classList.remove('focused');
    });
  }
}

export const iamLogin = new IamLogin();

// for access via html files and auth.js
(window as any).iam.Login = {
  initializePage: iamLogin.initializePage,
  handleAuthentication: iamLogin.handleAuthentication,
  parseQuery: iamLogin.parseQuery,
  appendTokenUrl: iamLogin.appendTokenUrl
};
Object.defineProperty((window as any).iam.Login, 'storageBlocked', {
  get: () => iamLogin.isEmailPage() && !iamLogin.storageEnabled
});
